// ============================================================
// FILE: Navbar.js
// LOCATION: C:\gardenmarket\web\src\components\Navbar.js
// ============================================================
// PURPOSE:
//   Top navigation bar rendered on every page (mounted once in
//   App.js, above the <Routes> block). Shows different links
//   depending on whether someone is logged in, and if so, which
//   user_type they are (buyer / gardener / admin).
//
//   ALSO renders a persistent "debug strip" beneath the main nav
//   showing the logged-in user's full name, user_id, and (for
//   gardeners) gardener_id. Added after repeated testing sessions
//   where it was hard to tell which test account/ID combination
//   was active, making bug reports ambiguous ("which job? which
//   gardener?"). This strip makes that immediately visible on
//   every page without needing to open dev tools or query SQL.
//
// USED BY:
//   App.js — rendered unconditionally above <Routes>
//
// NOTES FOR MAINTAINERS:
//   - The debug strip only renders when a user is logged in.
//   - REMOVE OR HIDE THIS STRIP BEFORE A PUBLIC/PRODUCTION LAUNCH —
//     it's a development/testing aid, not something real users
//     should see. Search for "DEBUG STRIP" below to find the
//     block to remove or wrap in a NODE_ENV !== 'production' check.
// ============================================================

import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <>
            <nav style={styles.nav}>
                <Link to="/" style={styles.brand}>🌿 GardenHand</Link>

                <div style={styles.links}>
                    {/* ── Logged-out menu ── */}
                    {!user && <>
                        <Link to="/login"    style={styles.link}>Login</Link>
                        <Link to="/register" style={styles.link}>Sign Up</Link>
                    </>}

                    {/* ── Buyer menu ── */}
                    {user?.user_type === 'buyer' && <>
                        <Link to="/buyer/dashboard" style={styles.link}>My Jobs</Link>
                        <Link to="/buyer/post-job"  style={styles.link}>Post a Job</Link>
                        <Link to="/gardeners"       style={styles.link}>Find Gardeners</Link>
                    </>}

                    {/* ── Gardener menu ── */}
                    {user?.user_type === 'gardener' && <>
                        <Link to="/gardener/dashboard" style={styles.link}>My Jobs</Link>
                        <Link to="/gardener/bookings"  style={styles.link}>My Bookings</Link>
                        <Link to="/gardener/profile"   style={styles.link}>My Profile</Link>
                    </>}

                    {/* ── Admin menu ── */}
                    {user?.user_type === 'admin' && <>
                        <Link to="/admin/dashboard" style={styles.link}>Admin</Link>
                    </>}

                    {/* ── Shown for any logged-in user ── */}
                    {user && <>
                        <span style={styles.username}>Hi, {user.first_name}</span>
                        <button onClick={handleLogout} style={styles.logoutBtn}>
                            Logout
                        </button>
                    </>}
                </div>
            </nav>

            {/* ── DEBUG STRIP — dev/testing aid, remove before launch ── */}
            {user && (
                <div style={styles.debugStrip}>
                    <span style={styles.debugItem}>
                        👤 <strong>{user.first_name} {user.last_name}</strong>
                    </span>
                    <span style={styles.debugItem}>user_id: <strong>{user.user_id}</strong></span>
                    {user.gardener_id && (
                        <span style={styles.debugItem}>gardener_id: <strong>{user.gardener_id}</strong></span>
                    )}
                    <span style={styles.debugItem}>type: <strong>{user.user_type}</strong></span>
                    {user.suburb_id && (
                        <span style={styles.debugItem}>suburb_id: <strong>{user.suburb_id}</strong> ({user.suburb_name})</span>
                    )}
                </div>
            )}
        </>
    );
}

// ── Inline styles (kept local to this component) ──────────
const styles = {
    nav: {
        display:         'flex',
        justifyContent:  'space-between',
        alignItems:      'center',
        padding:         '12px 24px',
        backgroundColor: '#2d6a4f',
        color:           'white',
        boxShadow:       '0 2px 4px rgba(0,0,0,0.2)',
    },
    brand: {
        color:          'white',
        textDecoration: 'none',
        fontSize:       '1.3rem',
        fontWeight:     'bold',
    },
    links: {
        display:    'flex',
        alignItems: 'center',
        gap:        '16px',
    },
    link: {
        color:          'white',
        textDecoration: 'none',
        fontSize:       '0.95rem',
    },
    username: {
        color:    '#b7e4c7',
        fontSize: '0.9rem',
    },
    logoutBtn: {
        background:   'transparent',
        border:       '1px solid white',
        color:        'white',
        padding:      '4px 12px',
        borderRadius: '4px',
        cursor:       'pointer',
        fontSize:     '0.9rem',
    },
    // Debug strip — distinct dark/yellow styling so it's visually
    // obvious this is a dev aid, not part of the real product UI
    debugStrip: {
        display:         'flex',
        gap:             '20px',
        flexWrap:        'wrap',
        padding:         '6px 24px',
        backgroundColor: '#fff3cd',
        borderBottom:    '1px solid #ffc107',
        fontSize:        '0.78rem',
        color:           '#856404',
    },
    debugItem: {
        whiteSpace: 'nowrap',
    },
};
